import { FC, useState, useMemo, useEffect } from "react";
import { Button, Input, Tooltip, message, Popconfirm, Badge } from "antd";
import {
  PanelRightOpen,
  PanelRightClose,
  History,
  FileText,
  Copy,
  Download,
  Clock,
  DollarSign,
  StickyNote,
  RotateCcw,
  Trash2,
  Mail,
  Table,
  AlertTriangle,
} from "lucide-react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);

// Types
export interface EstimateVersion {
  id: string;
  savedAt: string;
  total: number;
  notes?: string;
  config: any;
  versionNumber?: number; // From new table
  savedBy?: { // From new table
    id: string;
    firstName?: string;
    lastName?: string;
  };
  changeSummary?: any; // From new table
}

export interface EstimateVersionHistory {
  currentConfig: any;
  versions: EstimateVersion[];
}

interface EstimateSidebarProps {
  booking: any;
  invoiceData: any;
  versions: EstimateVersion[];
  currentTotal: number;
  onSave: (notes?: string) => Promise<void>;
  onRestoreVersion: (version: EstimateVersion) => void;
  onDeleteVersion: (versionId: string) => void;
  onPrint: () => void;
  hasUnsavedChanges: boolean;
  isSaving?: boolean;
  currentServices?: any[]; // Current services to check for deleted ones
  onSidebarToggle?: (isOpen: boolean) => void; // Callback when sidebar opens/closes
}

// Format currency helper
const formatCurrency = (amount: number | undefined): string => {
  if (amount === undefined) return "$0.00";
  return `$${Number(amount ?? 0)
    .toFixed(2)
    .replace(/\d(?=(\d{3})+\.)/g, "$&,")}`;
};

export const EstimateSidebar: FC<EstimateSidebarProps> = ({
  booking,
  invoiceData,
  versions,
  currentTotal,
  onSave,
  onRestoreVersion,
  onDeleteVersion,
  onPrint,
  hasUnsavedChanges,
  isSaving = false,
  currentServices = [],
  onSidebarToggle,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [saveNotes, setSaveNotes] = useState("");
  const [showSaveNotes, setShowSaveNotes] = useState(false);

  // Notify parent when sidebar state changes
  useEffect(() => {
    onSidebarToggle?.(isOpen);
  }, [isOpen, onSidebarToggle]);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  // Check if a version has deleted/removed services
  const hasDeletedServices = (version: EstimateVersion): boolean => {
    if (!version.config?.selectedServices || !Array.isArray(version.config.selectedServices)) {
      return false;
    }
    
    const currentServiceIds = new Set(currentServices.map(s => s.id));
    const versionServiceIds = version.config.selectedServices;
    
    // Check if any service in the version no longer exists
    return versionServiceIds.some((serviceId: string) => !currentServiceIds.has(serviceId));
  };

  // Get count of deleted services for a version
  const getDeletedServicesCount = (version: EstimateVersion): number => {
    if (!version.config?.selectedServices || !Array.isArray(version.config.selectedServices)) {
      return 0;
    }
    
    const currentServiceIds = new Set(currentServices.map(s => s.id));
    const versionServiceIds = version.config.selectedServices;
    
    return versionServiceIds.filter((serviceId: string) => !currentServiceIds.has(serviceId)).length;
  };

  // Generate plain text estimate for email
  const generatePlainTextEstimate = useMemo(() => {
    if (!invoiceData) return "";

    const lines: string[] = [];

    lines.push("═══════════════════════════════════════════");
    lines.push("           ART HANDLER COLLECTIVE");
    lines.push("              SERVICE ESTIMATE");
    lines.push("═══════════════════════════════════════════");
    lines.push("");

    // Header info
    lines.push(`Reference: ${booking?.referenceNumber || "N/A"}`);
    lines.push(`Date: ${dayjs().format("MMMM D, YYYY")}`);
    if (booking?.project?.name) {
      lines.push(`Project: ${booking.project.name}`);
    }
    lines.push("");

    // Client info
    if (invoiceData.client) {
      lines.push("CLIENT:");
      lines.push("───────────────────────────────────────────");
      if (invoiceData.client.companyName) {
        lines.push(`  ${invoiceData.client.companyName}`);
      }
      if (invoiceData.client.firstName || invoiceData.client.lastName) {
        lines.push(`  ${invoiceData.client.firstName || ""} ${invoiceData.client.lastName || ""}`.trim());
      }
      if (invoiceData.client.email) {
        lines.push(`  ${invoiceData.client.email}`);
      }
      lines.push("");
    }

    // Services
    lines.push("SERVICES:");
    lines.push("───────────────────────────────────────────");

    invoiceData.services?.forEach((service: any, index: number) => {
      lines.push("");
      lines.push(`${index + 1}. ${service.serviceType} - #${service.serial}`);
      lines.push(`   Date: ${dayjs(service.serviceDatetime).format("ddd, MMM D, YYYY")}`);
      if (service.location?.city) {
        lines.push(`   Location: ${service.location.city}, ${service.location.state || ""}`);
      }
      if (service.scope) {
        lines.push(`   Scope: ${service.scope}`);
      }
      if (service.duration) {
        lines.push(`   Site time: ${service.duration} hr${service.duration !== 1 ? "s" : ""}`);
      }
      if (service.addOnServices?.length > 0) {
        lines.push(`   Add-ons: ${service.addOnServices.join(", ")}`);
      }
      lines.push(`   Amount: ${formatCurrency(service.finalCost)}`);
    });

    lines.push("");

    // Additional charges (custom line items + supplies)
    if (invoiceData.additionalCharges?.length > 0) {
      lines.push("ADDITIONAL CHARGES:");
      lines.push("───────────────────────────────────────────");
      invoiceData.additionalCharges.forEach((charge: any) => {
        const dateStr = charge.date ? ` (${dayjs(charge.date).format("MM/DD")})` : "";
        lines.push(`  ${charge.description}${dateStr}: ${charge.quantity} × ${formatCurrency(charge.rate)} = ${formatCurrency(charge.total)}`);
        if (charge.notes) {
          lines.push(`    → ${charge.notes}`);
        }
      });
      lines.push("");
    }

    // Admin charges
    if (invoiceData.adminCharges?.length > 0) {
      lines.push("ADMINISTRATIVE CHARGES:");
      lines.push("───────────────────────────────────────────");
      invoiceData.adminCharges.forEach((charge: any) => {
        lines.push(`  ${charge.name}: ${formatCurrency(charge.total)}`);
      });
      lines.push("");
    }

    // Totals
    lines.push("═══════════════════════════════════════════");
    lines.push(`SUBTOTAL:              ${formatCurrency(invoiceData.financial?.subtotal)}`);
    if (invoiceData.financial?.depositAmount > 0) {
      lines.push(`DEPOSIT REQUESTED:     ${formatCurrency(invoiceData.financial.depositAmount)}`);
      lines.push(`BALANCE DUE:           ${formatCurrency(invoiceData.financial.balanceDue)}`);
    }
    lines.push(`TOTAL ESTIMATE:        ${formatCurrency(invoiceData.financial?.subtotal)}`);
    lines.push("═══════════════════════════════════════════");
    lines.push("");
    lines.push(`Payment Terms: ${invoiceData.financial?.paymentTerms || "Due Before Delivery"}`);
    lines.push("");
    lines.push("This is an estimate only. Final charges may vary based on");
    lines.push("actual services rendered.");
    lines.push("");
    lines.push("Thank you for choosing Art Handler Collective!");

    return lines.join("\n");
  }, [invoiceData, booking]);

  // Generate HTML table for email
  const generateHtmlEstimate = useMemo(() => {
    if (!invoiceData) return "";

    const html = `
<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
  <div style="text-align: center; padding: 20px; background: #f5f5f5; border-bottom: 3px solid #2563eb;">
    <h1 style="margin: 0; color: #1f2937; font-size: 24px;">Art Handler Collective</h1>
    <h2 style="margin: 5px 0 0; color: #6b7280; font-size: 16px; font-weight: normal;">Service Estimate</h2>
  </div>

  <div style="padding: 20px;">
    <table style="width: 100%; margin-bottom: 20px;">
      <tr>
        <td style="color: #6b7280;">Reference:</td>
        <td style="text-align: right; font-weight: bold;">${booking?.referenceNumber || "N/A"}</td>
      </tr>
      <tr>
        <td style="color: #6b7280;">Date:</td>
        <td style="text-align: right;">${dayjs().format("MMMM D, YYYY")}</td>
      </tr>
      ${booking?.project?.name ? `
      <tr>
        <td style="color: #6b7280;">Project:</td>
        <td style="text-align: right;">${booking.project.name}</td>
      </tr>` : ""}
    </table>

    <h3 style="color: #1f2937; border-bottom: 1px solid #e5e7eb; padding-bottom: 8px;">Services</h3>
    <table style="width: 100%; border-collapse: collapse; margin-bottom: 20px;">
      <thead>
        <tr style="background: #f9fafb;">
          <th style="text-align: left; padding: 10px; border-bottom: 1px solid #e5e7eb;">Service</th>
          <th style="text-align: left; padding: 10px; border-bottom: 1px solid #e5e7eb;">Date</th>
          <th style="text-align: right; padding: 10px; border-bottom: 1px solid #e5e7eb;">Amount</th>
        </tr>
      </thead>
      <tbody>
        ${invoiceData.services?.map((service: any) => `
        <tr>
          <td style="padding: 10px; border-bottom: 1px solid #f3f4f6;">
            <div style="font-weight: 500;">${service.serviceType}</div>
            <div style="font-size: 12px; color: #6b7280;">#${service.serial}${service.scope ? ` - ${service.scope}` : ""}${service.duration ? ` • ${service.duration} hr${service.duration !== 1 ? "s" : ""} on site` : ""}</div>
          </td>
          <td style="padding: 10px; border-bottom: 1px solid #f3f4f6; color: #6b7280;">
            ${dayjs(service.serviceDatetime).format("MMM D, YYYY")}
          </td>
          <td style="padding: 10px; border-bottom: 1px solid #f3f4f6; text-align: right; font-weight: 500;">
            ${formatCurrency(service.finalCost)}
          </td>
        </tr>
        `).join("") || ""}
        ${invoiceData.additionalCharges?.length > 0 ? `
        <tr style="background: #faf5ff;">
          <td colspan="3" style="padding: 8px 10px; font-weight: 600; color: #7c3aed; font-size: 12px;">Additional Charges</td>
        </tr>
        ${invoiceData.additionalCharges.map((charge: any) => `
        <tr>
          <td style="padding: 10px; border-bottom: 1px solid #f3f4f6;">
            <div style="font-weight: 500;">${charge.description}</div>
            <div style="font-size: 12px; color: #6b7280;">${charge.type === 'supply' ? 'Supply' : 'Custom'}${charge.date ? ` - ${dayjs(charge.date).format("MMM D")}` : ""}</div>
            ${charge.notes ? `<div style="font-size: 11px; color: #4b5563; margin-top: 4px;">${charge.notes}</div>` : ""}
          </td>
          <td style="padding: 10px; border-bottom: 1px solid #f3f4f6; color: #6b7280;">
            ${charge.quantity} × ${formatCurrency(charge.rate)}
          </td>
          <td style="padding: 10px; border-bottom: 1px solid #f3f4f6; text-align: right; font-weight: 500;">
            ${formatCurrency(charge.total)}
          </td>
        </tr>
        `).join("")}` : ""}
        ${invoiceData.adminCharges?.length > 0 ? `
        <tr style="background: #f0fdf4;">
          <td colspan="3" style="padding: 8px 10px; font-weight: 600; color: #16a34a; font-size: 12px;">Administrative Charges</td>
        </tr>
        ${invoiceData.adminCharges.map((charge: any) => `
        <tr>
          <td style="padding: 10px; border-bottom: 1px solid #f3f4f6;" colspan="2">
            <div style="font-weight: 500;">${charge.name}</div>
            <div style="font-size: 12px; color: #6b7280;">${charge.quantity} × ${formatCurrency(charge.rate)}</div>
          </td>
          <td style="padding: 10px; border-bottom: 1px solid #f3f4f6; text-align: right; font-weight: 500;">
            ${formatCurrency(charge.total)}
          </td>
        </tr>
        `).join("")}` : ""}
      </tbody>
      <tfoot>
        <tr style="background: #f9fafb;">
          <td colspan="2" style="padding: 12px; font-weight: bold; text-align: right;">Total Estimate:</td>
          <td style="padding: 12px; font-weight: bold; text-align: right; font-size: 18px; color: #2563eb;">
            ${formatCurrency(invoiceData.financial?.subtotal)}
          </td>
        </tr>
        ${invoiceData.financial?.depositAmount > 0 ? `
        <tr>
          <td colspan="2" style="padding: 8px; text-align: right; color: #6b7280;">Deposit Requested:</td>
          <td style="padding: 8px; text-align: right; font-weight: 500;">${formatCurrency(invoiceData.financial.depositAmount)}</td>
        </tr>
        <tr>
          <td colspan="2" style="padding: 8px; text-align: right; color: #6b7280;">Balance Due:</td>
          <td style="padding: 8px; text-align: right; font-weight: 500;">${formatCurrency(invoiceData.financial.balanceDue)}</td>
        </tr>
        ` : ""}
      </tfoot>
    </table>

    <div style="background: #f9fafb; padding: 15px; border-radius: 8px; margin-top: 20px;">
      <p style="margin: 0; color: #6b7280; font-size: 14px;">
        <strong>Payment Terms:</strong> ${invoiceData.financial?.paymentTerms || "Due Before Delivery"}
      </p>
      <p style="margin: 10px 0 0; color: #9ca3af; font-size: 12px;">
        This is an estimate only. Final charges may vary based on actual services rendered. Deposits may be required prior to service date.
      </p>
    </div>
  </div>

  <div style="text-align: center; padding: 15px; background: #f5f5f5; color: #6b7280; font-size: 12px;">
    Thank you for choosing Art Handler Collective!
  </div>
</div>`;

    return html;
  }, [invoiceData, booking]);

  // Copy to clipboard handlers
  const copyPlainText = async () => {
    try {
      await navigator.clipboard.writeText(generatePlainTextEstimate);
      message.success("Plain text estimate copied to clipboard!");
    } catch (err) {
      message.error("Failed to copy to clipboard");
    }
  };

  const copyHtmlTable = async () => {
    try {
      // Create a blob with HTML content
      const blob = new Blob([generateHtmlEstimate], { type: "text/html" });
      const plainBlob = new Blob([generatePlainTextEstimate], { type: "text/plain" });

      // Use ClipboardItem for rich text copy
      await navigator.clipboard.write([
        new ClipboardItem({
          "text/html": blob,
          "text/plain": plainBlob,
        }),
      ]);
      message.success("HTML estimate copied to clipboard - paste into email!");
    } catch (err) {
      // Fallback to plain HTML string
      try {
        await navigator.clipboard.writeText(generateHtmlEstimate);
        message.success("HTML copied to clipboard (paste as code)");
      } catch (e) {
        message.error("Failed to copy to clipboard");
      }
    }
  };

  // Handle save with notes
  const handleSave = async () => {
    await onSave(saveNotes || undefined);
    setSaveNotes("");
    setShowSaveNotes(false);
  };

  return (
    <>
      {/* Toggle Button */}
      <Tooltip title={isOpen ? "Close sidebar" : "Open estimate tools"}>
        <button
          onClick={handleToggle}
          className={`fixed right-0 top-1/2 -translate-y-1/2 z-40 p-3 rounded-l-lg shadow-lg transition-all ${
            isOpen ? "bg-blue-600 text-white" : "bg-white text-gray-700 border border-r-0"
          } hover:shadow-xl`}
        >
          {isOpen ? <PanelRightClose size={20} /> : <PanelRightOpen size={20} />}
        </button>
      </Tooltip>

      {/* Sliding Sidebar - Pushes content instead of overlaying */}
      <div
        className={`fixed right-0 top-0 h-full z-40 bg-white shadow-2xl transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{ width: isOpen ? '360px' : '0px' }}
      >
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b">
            <div className="flex items-center gap-2">
              <FileText size={20} className="text-blue-600" />
              <span className="font-medium">Estimate Tools</span>
            </div>
            <button
              onClick={handleToggle}
              className="p-1 hover:bg-gray-100 rounded transition-colors"
            >
              <PanelRightClose size={20} />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4">
            <div className="space-y-6">
              {/* Current Estimate Summary */}
              <div className="bg-blue-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600">Current Estimate</span>
                  {hasUnsavedChanges && (
                    <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded">
                      Unsaved
                    </span>
                  )}
                </div>
                <div className="text-2xl font-bold text-blue-700">
                  {formatCurrency(currentTotal)}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  {booking?.referenceNumber} • {dayjs().format("MMM D, YYYY")}
                </div>
              </div>

              {/* Save Section */}
              <div className="border rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
              <DollarSign size={16} />
              Save Estimate
            </h4>

            {showSaveNotes ? (
              <div className="space-y-3">
                <Input.TextArea
                  value={saveNotes}
                  onChange={(e) => setSaveNotes(e.target.value)}
                  placeholder="Add notes about this version (optional)..."
                  rows={2}
                  className="text-sm"
                />
                <div className="flex gap-2">
                  <Button
                    type="primary"
                    onClick={handleSave}
                    loading={isSaving}
                    className="flex-1"
                  >
                    Save Version
                  </Button>
                  <Button onClick={() => setShowSaveNotes(false)}>
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <Button
                type={hasUnsavedChanges ? "primary" : "default"}
                onClick={() => setShowSaveNotes(true)}
                block
                className={!hasUnsavedChanges ? "border-green-500 text-green-600" : ""}
              >
                    {hasUnsavedChanges ? "Save Changes" : "✓ Up to date"}
              </Button>
            )}
              </div>

              {/* Export Section */}
              <div className="border rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
              <Mail size={16} />
              Export & Share
            </h4>

            <div className="space-y-2">
              <Button
                icon={<Download size={16} />}
                onClick={onPrint}
                block
                className="flex items-center justify-center gap-2"
              >
                Download PDF
              </Button>

              <div className="grid grid-cols-2 gap-2">
                <Tooltip title="Copy plain text version for any email client">
                  <Button
                    icon={<Copy size={14} />}
                    onClick={copyPlainText}
                    size="small"
                    disabled={!invoiceData}
                    className="flex items-center justify-center gap-1 text-xs"
                  >
                    Plain Text
                  </Button>
                </Tooltip>

                <Tooltip title="Copy formatted HTML table for rich email clients">
                  <Button
                    icon={<Table size={14} />}
                    onClick={copyHtmlTable}
                    size="small"
                    disabled={!invoiceData}
                    className="flex items-center justify-center gap-1 text-xs"
                  >
                    HTML Table
                  </Button>
                </Tooltip>
              </div>

              {!invoiceData && (
                <p className="text-xs text-gray-400 text-center mt-2">
                  Select services to enable copy options
                </p>
              )}
              </div>
              </div>

              {/* Version History */}
              <div className="border rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
              <History size={16} />
              Version History
              {versions.length > 0 && (
                <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                  {versions.length}
                </span>
              )}
            </h4>

            {versions.length > 0 ? (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {versions
                  .slice()
                  .sort((a, b) => new Date(b.savedAt).getTime() - new Date(a.savedAt).getTime())
                  .map((version, index) => (
                    <div
                      key={version.id}
                      className={`bg-gray-50 rounded-lg p-3 hover:bg-gray-100 transition-colors ${
                        hasDeletedServices(version) ? 'border border-orange-200 bg-orange-50' : ''
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Clock size={12} className="text-gray-400 flex-shrink-0" />
                            <span className="text-xs text-gray-500">
                              {dayjs(version.savedAt).fromNow()}
                            </span>
                            {index === 0 && (
                              <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
                                Latest
                              </span>
                            )}
                            {hasDeletedServices(version) && (
                              <Tooltip 
                                title={`This version includes ${getDeletedServicesCount(version)} service(s) that have been removed`}
                              >
                                <Badge 
                                  count={<AlertTriangle size={10} className="text-orange-600" />}
                                  className="cursor-help"
                                >
                                  <span className="text-xs bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded flex items-center gap-1">
                                    <AlertTriangle size={10} />
                                    {getDeletedServicesCount(version)} removed
                                  </span>
                                </Badge>
                              </Tooltip>
                            )}
                          </div>
                          <div className="font-medium text-gray-900 mt-1">
                            {formatCurrency(version.total)}
                          </div>
                          {version.notes && (
                            <div className="flex items-start gap-1 mt-1">
                              <StickyNote size={10} className="text-gray-400 mt-0.5 flex-shrink-0" />
                              <span className="text-xs text-gray-600 line-clamp-2">
                                {version.notes}
                              </span>
                            </div>
                          )}
                          {hasDeletedServices(version) && (
                            <div className="text-xs text-orange-600 mt-1 flex items-center gap-1">
                              <AlertTriangle size={10} />
                              Some services in this version no longer exist
                            </div>
                          )}
                          <div className="text-xs text-gray-400 mt-1">
                            {dayjs(version.savedAt).format("MMM D, YYYY h:mm A")}
                          </div>
                        </div>

                        <div className="flex items-center gap-1 ml-2">
                          <Tooltip title="Restore this version">
                            <button
                              onClick={() => onRestoreVersion(version)}
                              className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                            >
                              <RotateCcw size={14} />
                            </button>
                          </Tooltip>
                          <Popconfirm
                            title="Delete this version?"
                            description="This cannot be undone."
                            onConfirm={() => onDeleteVersion(version.id)}
                            okText="Delete"
                            cancelText="Cancel"
                            okButtonProps={{ danger: true }}
                          >
                            <button className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors">
                              <Trash2 size={14} />
                            </button>
                          </Popconfirm>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            ) : (
              <div className="text-center py-6 text-gray-400">
                <History size={32} className="mx-auto mb-2 opacity-50" />
                <p className="text-sm">No saved versions yet</p>
                <p className="text-xs">Save your estimate to track changes</p>
              </div>
            )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
